import { roundFishingCoordinate } from "../fishingToday/fishingTodayLocation";

export const ACCESS_CONTEXTS = Object.freeze([
  "rocky_shore",
  "beach_surf",
  "pier_jetty",
  "harbour",
  "tidal_flat_mudflat",
  "riverbank",
  "lakebank",
  "vegetated_reed_edge",
  "open_water",
  "unknown",
]);

export const HABITAT_FEATURES = Object.freeze([
  "rock",
  "sand",
  "mud_silt",
  "gravel",
  "weed_seagrass",
  "reef",
  "wreck_structure",
  "drop_off",
  "channel",
  "shallow_flat",
  "deep_water",
  "current_seam",
  "cover",
  "vegetation",
]);

const accessSet = new Set(ACCESS_CONTEXTS);
const habitatSet = new Set(HABITAT_FEATURES);
const marineAccessPriority = [
  "rocky_shore",
  "beach_surf",
  "pier_jetty",
  "harbour",
  "tidal_flat_mudflat",
  "open_water",
];
const inlandAccessPriority = [
  "lakebank",
  "riverbank",
  "vegetated_reed_edge",
  "beach_surf",
  "pier_jetty",
  "open_water",
];
const riverAccessPriority = [
  "riverbank",
  "vegetated_reed_edge",
  "open_water",
  "harbour",
  "tidal_flat_mudflat",
];
const marineCommonHabitat = [
  "rock",
  "sand",
  "mud_silt",
  "reef",
  "weed_seagrass",
  "channel",
  "shallow_flat",
  "deep_water",
];
const inlandCommonHabitat = [
  "weed_seagrass",
  "vegetation",
  "cover",
  "channel",
  "shallow_flat",
  "deep_water",
  "mud_silt",
  "gravel",
];

const prioritized = (allValues, firstValues) => [
  ...firstValues,
  ...allValues.filter((value) => !firstValues.includes(value)),
];

export const emptyFishingSetting = () => ({
  habitatFeatures: [],
  accessContext: null,
});

export const hasFishingSetting = (value) => {
  const normalized = normalizeFishingSetting(value);
  return (
    normalized.accessContext !== null || normalized.habitatFeatures.length > 0
  );
};

export const normalizeFishingSetting = (value = {}) => ({
  habitatFeatures: Array.from(
    new Set(
      (Array.isArray(value?.habitatFeatures) ? value.habitatFeatures : []).filter(
        (feature) => habitatSet.has(feature)
      )
    )
  ).sort(),
  accessContext: accessSet.has(value?.accessContext) ? value.accessContext : null,
});

export const fishingSettingLocationKey = (location, outlookLocationKey = "") => {
  if (
    location?.source === "favorite_spot" &&
    typeof location.identityKey === "string" &&
    location.identityKey
  ) {
    return `favorite:${location.identityKey}`;
  }
  if (
    location?.source === "live_gps" &&
    Number.isFinite(location.latitude) &&
    Number.isFinite(location.longitude)
  ) {
    return `live:${roundFishingCoordinate(location.latitude)},${roundFishingCoordinate(
      location.longitude
    )}`;
  }
  return `profile:${String(outlookLocationKey || "")}`;
};

export const accessContextsForWaterbody = (waterbody) => {
  if (["river", "stream", "canal", "estuary_brackish"].includes(waterbody)) {
    return prioritized(ACCESS_CONTEXTS, riverAccessPriority);
  }
  if (["freshwater", "lake_reservoir", "pond"].includes(waterbody)) {
    return prioritized(ACCESS_CONTEXTS, inlandAccessPriority);
  }
  return prioritized(ACCESS_CONTEXTS, marineAccessPriority);
};

export const habitatGroupsForWaterbody = (waterbody) => {
  const common = ["freshwater", "river", "stream", "lake_reservoir", "canal", "pond"].includes(
    waterbody
  )
    ? inlandCommonHabitat
    : marineCommonHabitat;
  return {
    common,
    advanced: HABITAT_FEATURES.filter((feature) => !common.includes(feature)),
  };
};

export const accessLabelKey = (value) => `fishing_today_setting_access_${value}`;
export const habitatLabelKey = (value) => `fishing_today_setting_habitat_${value}`;
